import React, { useContext } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Grid, Header, Segment, Button, Table, Icon } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CoolFarmCropOptionsContext } from '../../index'
import { useTranslation } from 'react-i18next'
import Fertilizer from './Fertilizer'
import InfoPopup from '~/components/InfoPopup'

const Inputs: React.FC = () => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const options = useContext(CoolFarmCropOptionsContext)

  const { fields: fertilizerFields, append: appendFertilizer, remove: removeFertilizer } = useFieldArray({
    control,
    name: 'fertilizers',
    keyName: 'key'
  })

  const { fields: protectionFields, append: appendProtection, remove: removeProtection } = useFieldArray({
    control,
    name: 'protections',
    keyName: 'key'
  })

  const protectionCategoryOptions = options?.cropProtectionCategories?.map(cat => ({
    key: cat.key,
    value: cat.key,
    text: cat.label
  })) || []

  const protectionTypeOptions = options?.cropProtectionTypes?.map(type => ({
    key: type.key,
    value: type.key,
    text: type.label
  })) || []

  const amountUnitOptions = options?.fertilizerRateUnits?.map(unit => ({
    key: unit.key,
    value: unit.key,
    text: unit.label
  })) || []

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
          <Header attached='top' block>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {t('Fertiliser inputs')}
              <InfoPopup
                style={{ minWidth: 400 }}
                content={t(`Enter data on fertiliser application rate per unit of area.
                  Note1: You can enter inputs by 'fertiliser weight' or by 'units' of the active ingredient, such as units of N (nitrogen). Note2: If there are multiple applications of a unique product in a
                  single month you can aggregate to a single "Fertiliser application". But please create a new "Fertilizer Application" for each month that a product is applied and use the midpoint of the
                  month as the date of application. Results will be more accurate if each unique application of a product is recorded separately.`)}
                styleIcon={{ fontSize: '1.3em' }}
              />
            </div>
          </Header>
          <Segment attached>
            <p>
              {t(
                `Please provide information regarding the application rate of fertiliser per unit of growing area. 
                Please keep in mind that you have the option to input data either based on fertiliser weight
                or in terms of units of the active ingredient, such as nitrogen (N).`
              )}
            </p>

            <p style={{ marginTop: '1em' }}>
              {t(
                'If you cannot find your specific fertiliser type listed, you have two choices:'
              )}
            </p>

            <ul style={{ margin: 0 }}>
              <li>
                {t(
                  'Select the fertiliser that closely matches your own.'
                )}
              </li>
              <li>
                {t(
                  'Select \'Compose your own NPK\' and create the fertiliser by indicating the percentage of active ingredients and the nitrogen (N) composition.'
                )}
              </li>
            </ul>
            {fertilizerFields.map((field, index) => (
              <Segment key={field.key} style={{ marginBottom: '1em' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
                  <Header as='h4'>{t('Fertiliser Application')} {index + 1}</Header>
                  <Icon
                    name='trash'
                    color='red'
                    size='large'
                    style={{ cursor: 'pointer' }}
                    onClick={() => removeFertilizer(index)}
                  />
                </div>
                <Fertilizer
                  index={index}
                />
              </Segment>
            ))}
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
              <Button
                type='button'
                icon='plus'
                style={{ color: 'white' }}
                content={t('Add Fertilizer')}
                onClick={() => appendFertilizer({
                  cftIngredientId: null,
                  amount: null,
                  amountUnit: null,
                  cftProductionRegionId: null,
                  applicationDate: '',
                  applicationMethod: null,
                  emissionsInhibitors: 0
                })}
              />
            </div>
          </Segment>
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={16}>
          <Header attached='top' block>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {t('Crop protection inputs')}
              <InfoPopup
                style={{ minWidth: 400 }}
                content={t(`Fewer options are available for crop protection inputs compared to fertilisers because less environmental data is available,
                  so average emission factor values are used. Crop protection inputs generally contribute a lower proportion of emissions than fertilisers.`)}
                styleIcon={{ fontSize: '1.3em' }}
              />
            </div>
          </Header>
          <Segment attached>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t('Category')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Type')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Application Rate')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Application Rate Unit')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Active ingredient %')}</Table.HeaderCell>
                  <Table.HeaderCell>{t('Actions')}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {protectionFields.map((field, index) => (
                  <Table.Row key={field.key}>
                    <Table.Cell>
                      <FormFields.Select
                        control={control}
                        name={`protections[${index}].category`}
                        withoutAsterisk
                        options={protectionCategoryOptions}
                        placeholder={t('Select category')}
                        noErrorMessage
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <FormFields.Select
                        control={control}
                        name={`protections[${index}].type`}
                        withoutAsterisk
                        options={protectionTypeOptions}
                        placeholder={t('Select type')}
                        noErrorMessage
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <FormFields.Input
                        control={control}
                        name={`protections[${index}].applicationRate`}
                        withoutAsterisk
                        placeholder={t('Rate')}
                        typeInput='controled'
                        allowDecimals
                        decimalScale={2}
                        noErrorMessage
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <FormFields.Select
                        control={control}
                        name={`protections[${index}].applicationRateUnit`}
                        withoutAsterisk
                        options={amountUnitOptions}
                        placeholder={t('Unit')}
                        noErrorMessage
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <FormFields.Input
                        control={control}
                        name={`protections[${index}].percentage`}
                        withoutAsterisk
                        placeholder={t('Percentage')}
                        typeInput='controled'
                        allowDecimals
                        decimalScale={2}
                        noErrorMessage
                      />
                    </Table.Cell>
                    <Table.Cell textAlign='center'>
                      <Icon
                        name='trash'
                        color='red'
                        style={{ cursor: 'pointer' }}
                        onClick={() => removeProtection(index)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: '1em' }}>
              <Button
                type='button'
                icon='plus'
                style={{ color: 'white' }}
                content={t('Add Protection')}
                onClick={() => appendProtection({
                  category: null,
                  type: null,
                  applicationRate: null,
                  applicationRateUnit: null,
                  percentage: null
                })}
              />
            </div>
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

export default Inputs
